import { Link, useParams } from "react-router-dom";
import {
  CaretLeft,
  Clock,
  Users,
  MapPin,
  Globe,
  Trophy,
  ShieldCheck,
  ArrowUpRight,
  SealCheck,
  CheckSquareOffset,
  Calendar,
} from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import { getCall, calls } from "@/data/mockData";

const typeColor = {
  RESIDENCIA: "#00FF66",
  BECA: "#FF3E00",
  PREMIO: "#FFD600",
  "OPEN CALL": "#0033FF",
  COMISIÓN: "#FF0033",
};

function deadlineTone(days) {
  if (days <= 3) return { color: "#FF0033", label: "URGENTE" };
  if (days <= 14) return { color: "#FF3E00", label: "PRONTO" };
  if (days <= 30) return { color: "#FFD600", label: "ESTE MES" };
  return { color: "#00FF66", label: "ABIERTA" };
}

export default function CallDetail() {
  const { id } = useParams();
  const call = getCall(id);
  const tone = deadlineTone(call.daysLeft);
  const related = calls
    .filter((c) => c.id !== call.id && c.discipline === call.discipline)
    .slice(0, 3);

  return (
    <Layout>
      <div data-testid="call-detail-page">
        {/* Hero */}
        <header className="relative overflow-hidden border-b border-[#27272A]">
          <div className="absolute inset-0">
            <img
              src={call.cover}
              alt=""
              className="w-full h-full object-cover grayscale opacity-50"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/80 to-[#050505]" />
          </div>
          <div className="relative p-5 md:p-10">
            <Link
              to="/convocatorias"
              data-testid="back-calls"
              className="h-9 w-9 border border-[#27272A] hover:border-[#FF3E00] inline-flex items-center justify-center"
            >
              <CaretLeft size={16} />
            </Link>

            <div className="mt-6 flex items-center gap-2 flex-wrap">
              <span
                className={`font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 ${
                  typeColor[call.type] === "#FF3E00" ? "text-white" : "text-black"
                }`}
                style={{ background: typeColor[call.type] }}
              >
                {call.type}
              </span>
              <span
                className={`font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 ${
                  tone.color === "#FF3E00" || tone.color === "#FF0033" ? "text-white" : "text-black"
                }`}
                style={{ background: tone.color }}
              >
                ● {tone.label} · {call.daysLeft} DÍAS
              </span>
              {call.verified ? (
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF66] border border-[#00FF66]/50 px-2 py-1 inline-flex items-center gap-1">
                  <SealCheck size={11} weight="fill" /> VERIFICADA
                </span>
              ) : null}
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">
                {call.discipline}
              </span>
            </div>

            <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider mt-5 mb-2">
              // CONVOCADA POR {call.org.toUpperCase()}
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.95] max-w-4xl">
              {call.title}
            </h1>

            {/* Quick specs */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-0 border border-[#27272A] max-w-4xl">
              <QuickSpec
                icon={Trophy}
                label="PREMIO"
                value={call.prize}
                color="#FF3E00"
              />
              <QuickSpec
                icon={Clock}
                label="CIERRE"
                value={`${call.daysLeft} días`}
                color={tone.color}
                divider
              />
              <QuickSpec
                icon={call.remote ? Globe : MapPin}
                label="LUGAR"
                value={call.location}
                divider
              />
              <QuickSpec
                icon={Users}
                label="POSTULANTES"
                value={`${call.applicants}`}
                divider
              />
            </div>
          </div>
        </header>

        {/* Body */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px]">
          <section className="p-5 md:p-8 border-b lg:border-b-0 lg:border-r border-[#27272A] space-y-10">
            {/* Summary */}
            <div>
              <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-3">
                RESUMEN
              </div>
              <p className="font-display text-xl md:text-2xl leading-[1.45] text-[#F4F4F5] max-w-3xl">
                {call.summary}
              </p>
            </div>

            {/* Requirements */}
            <div>
              <div className="flex items-center gap-2 mb-5">
                <CheckSquareOffset size={18} className="text-[#00FF66]" weight="fill" />
                <h2 className="font-display font-black uppercase tracking-tight text-xl">
                  Requisitos
                </h2>
              </div>
              <ul className="space-y-2 max-w-2xl">
                {call.requirements.map((r, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 p-3 border border-[#27272A] bg-[#0a0a0a]"
                  >
                    <span className="font-mono text-[10px] text-[#FF3E00] tracking-wider mt-0.5 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[15px] text-[#E5E5E5]">{r}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Jury */}
            <div>
              <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-3">
                // JURADO / COMITÉ
              </div>
              <div className="flex flex-wrap gap-2">
                {call.jury.map((j) => (
                  <span
                    key={j}
                    className="font-display font-semibold text-sm border border-[#27272A] px-3 py-2"
                  >
                    {j}
                  </span>
                ))}
              </div>
            </div>

            {/* Process */}
            <div>
              <div className="flex items-center gap-2 mb-5">
                <Calendar size={18} className="text-[#FF3E00]" weight="fill" />
                <h2 className="font-display font-black uppercase tracking-tight text-xl">
                  Proceso
                </h2>
              </div>
              <ol className="border-l-2 border-[#FF3E00] pl-5 space-y-5">
                {[
                  { t: "Preparación", d: "Arma tu portafolio y documentos requeridos." },
                  { t: "Postulación", d: "Completa el formulario oficial antes de la fecha." },
                  { t: "Shortlist", d: "Notificación de finalistas ~4 semanas después del cierre." },
                  { t: "Decisión final", d: "Anuncio público + contacto directo a ganadores." },
                ].map((step, i) => (
                  <li key={i} className="relative">
                    <span className="absolute -left-[29px] top-1 h-3 w-3 bg-[#050505] border-2 border-[#FF3E00]" />
                    <div className="font-mono text-[10px] text-[#FF3E00] tracking-wider">
                      PASO {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="font-display font-bold">{step.t}</div>
                    <div className="text-sm text-[#A1A1AA] mt-1">{step.d}</div>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* Apply sidebar */}
          <aside className="p-5 md:p-6 space-y-4">
            {/* Countdown */}
            <div
              className="border p-5 text-center"
              style={{ borderColor: tone.color, background: `${tone.color}10` }}
            >
              <div
                className="font-mono text-[10px] tracking-[0.22em]"
                style={{ color: tone.color }}
              >
                CIERRA EN
              </div>
              <div className="font-display font-black text-6xl md:text-7xl leading-none mt-2">
                {call.daysLeft}
                <span
                  className="text-2xl ml-1"
                  style={{ color: tone.color }}
                >
                  DÍAS
                </span>
              </div>
              <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider mt-3">
                DEADLINE · {call.deadline}
              </div>
            </div>

            <button
              data-testid="apply-btn"
              className="btn-brutal w-full bg-[#FF3E00] text-white font-mono uppercase text-xs tracking-[0.22em] font-bold py-4 inline-flex items-center justify-center gap-2"
            >
              Postular ahora <ArrowUpRight size={14} weight="bold" />
            </button>
            <button
              data-testid="save-call"
              className="btn-brutal w-full border border-[#F4F4F5] text-[#F4F4F5] hover:bg-[#F4F4F5] hover:text-black font-mono uppercase text-xs tracking-[0.22em] py-3"
            >
              ♥ Guardar para después
            </button>
            <button className="w-full font-mono uppercase text-[10px] tracking-[0.22em] text-[#A1A1AA] hover:text-white py-2">
              ↗ Compartir con alguien
            </button>

            <div className="border border-[#27272A] p-4 bg-[#0a0a0a]">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={14} className="text-[#00FF66]" />
                <span className="font-mono text-[10px] text-[#00FF66] tracking-[0.22em]">
                  POSTULACIÓN GRATUITA
                </span>
              </div>
              <p className="font-mono text-[11px] text-[#A1A1AA] tracking-wider leading-relaxed">
                ELARTE/NET NUNCA COBRA POR POSTULAR. SI ALGUIEN TE PIDE DINERO
                PARA APLICAR, REPÓRTALO.
              </p>
            </div>

            {call.community ? (
              <div className="border border-[#FF3E00] bg-[#FF3E00]/5 p-4">
                <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.22em] mb-2">
                  ✦ CONVOCATORIA COMUNITARIA
                </div>
                <p className="font-mono text-[11px] text-[#E5E5E5] tracking-wider leading-relaxed">
                  Esta convocatoria fue creada por un miembro de la comunidad.
                  Selección con votación abierta + curaduría editorial.
                </p>
              </div>
            ) : null}
          </aside>
        </div>

        {/* Related */}
        {related.length > 0 ? (
          <section className="p-4 md:p-6 border-t border-[#27272A]">
            <h2 className="font-display font-black uppercase tracking-tight text-xl mb-4">
              Convocatorias similares
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {related.map((c) => (
                <Link
                  key={c.id}
                  to={`/convocatorias/${c.id}`}
                  className="group block border border-[#27272A] hover:border-[#FF3E00] bg-[#0a0a0a] p-4"
                >
                  <div className="font-mono text-[10px] text-[#FF3E00] tracking-wider">
                    {c.type} · {c.daysLeft}D
                  </div>
                  <h3 className="font-display font-bold text-sm mt-2 line-clamp-2 leading-tight">
                    {c.title}
                  </h3>
                  <div className="mt-3 pt-3 border-t border-[#27272A] font-mono text-[10px] text-[#A1A1AA] tracking-wider flex items-center justify-between">
                    <span className="truncate">{c.org}</span>
                    <span className="text-[#FF3E00] whitespace-nowrap">
                      {c.prize.length > 18 ? c.prize.slice(0, 16) + "…" : c.prize}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </Layout>
  );
}

function QuickSpec({ icon: Icon, label, value, color, divider }) {
  return (
    <div
      className={`p-4 ${
        divider ? "border-l border-t md:border-t-0 border-[#27272A]" : ""
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
          {label}
        </span>
        <Icon size={13} style={color ? { color } : { color: "#A1A1AA" }} weight="fill" />
      </div>
      <div
        className="font-display font-black text-sm md:text-base leading-tight"
        style={color ? { color } : {}}
      >
        {value}
      </div>
    </div>
  );
}
